import { useSyncExternalStore } from 'react';
import { env } from './env';

/**
 * Whether the server can be reached right now (ADR-037).
 *
 *  - online:  the browser has a network and the server answered the last time it was asked;
 *  - offline: the browser has no network at all;
 *  - lost:    the browser has a network, but the server cannot be reached (the internet down behind
 *             the Wi-Fi, the connection dropped).
 *
 * Every request through the Supabase client reports here (`observedFetch`). One failed request is not
 * enough to call the connection lost (a dropped request happens): the server is asked again at once,
 * and only if that fails too is it lost. While lost, the server is asked every few seconds; the first
 * answer brings the app back online on its own.
 *
 * While not online the app is read-only: it shows what it last loaded, and nothing that records an
 * order, money, a shift or a kitchen action can be started (each needs the server now).
 */
export type Connectivity = 'online' | 'offline' | 'lost';

const browserOnline = () => typeof navigator === 'undefined' || navigator.onLine;

let state: Connectivity = browserOnline() ? 'online' : 'offline';
let since = Date.now();
const listeners = new Set<() => void>();
let probing = false;
let timer: ReturnType<typeof setTimeout> | null = null;
const PROBE_EVERY_MS = 5_000;
/** A connection that hangs (the internet down behind the Wi-Fi) is no answer after this long. */
const PROBE_TIMEOUT_MS = 4_000;
/** Counts the server's answers: a probe that started before one cannot undo it. */
let answers = 0;

function set(next: Connectivity) {
  if (next === state) return;
  state = next;
  since = Date.now();
  listeners.forEach((l) => l());
}

/** Ask the server something tiny: any HTTP answer at all means it can be reached. */
async function probe(): Promise<boolean> {
  // a timer, not AbortSignal.timeout: iOS 15 runs this build without it
  const give = new AbortController();
  const timeout = setTimeout(() => give.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(`${env.supabaseUrl}/auth/v1/health`, {
      headers: { apikey: env.supabaseKey },
      cache: 'no-store',
      signal: give.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

async function checkNow() {
  if (probing) return;
  probing = true;
  const before = answers;
  try {
    const ok = await probe();
    if (!browserOnline()) set('offline');
    else if (ok) set('online');
    // another request reached the server while this probe waited: that answer stands
    else if (answers === before) set('lost');
  } finally {
    probing = false;
  }
  schedule();
}

function schedule() {
  if (timer) clearTimeout(timer);
  timer = null;
  if (state === 'lost') timer = setTimeout(() => void checkNow(), PROBE_EVERY_MS);
}

/** A request reached the server (any HTTP status). */
export function reportServerReached() {
  answers += 1;
  if (state !== 'online' && browserOnline()) {
    set('online');
    schedule();
  }
}

/** A request failed for the network: check at once before calling the connection lost. */
export function reportNetworkFailure() {
  if (!browserOnline()) {
    set('offline');
    return;
  }
  void checkNow();
}

const isAbort = (e: unknown) => e instanceof DOMException && e.name === 'AbortError';

/** The fetch the Supabase client uses: the same request, its outcome reported here. */
export const observedFetch: typeof fetch = (input, init) =>
  fetch(input, init).then(
    (res) => {
      reportServerReached();
      return res;
    },
    (err: unknown) => {
      if (!isAbort(err)) reportNetworkFailure();
      throw err;
    },
  );

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => {
    set('offline');
    schedule();
  });
  // the network is back: the server may still be out of reach, so ask it before saying online
  window.addEventListener('online', () => void checkNow());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getState = () => state;

/** Called with the new state on every change (the outbox sends what it holds when the server is back). */
export function onConnectivityChange(listener: (next: Connectivity) => void): () => void {
  return subscribe(() => listener(state));
}

export function useConnectivity(): Connectivity {
  return useSyncExternalStore(subscribe, getState, getState);
}

/** When the current state began (for "offline for 2 min"). */
export function connectivitySince(): number {
  return since;
}

export function currentConnectivity(): Connectivity {
  return state;
}

/** What a paused action says while the server cannot be reached. */
export const PAUSED_REASON =
  'No connection to the server. This needs it now: it is paused until the connection is back.';
